import React from 'react';
import moment from 'moment';
import { useIntl } from 'react-intl';
import { Chip } from '@lifeomic/chroma-react/components/Chip';
import { Tooltip } from '@lifeomic/chroma-react/components/Tooltip';
import { messages } from './constants';
import {
  ExtractionConfidence,
  getExtractionConfidence,
} from './extractionConfidence';

type ExtractionConfidenceBadgeColor = 'positive' | 'warning' | 'negative';
type ExtractionConfidenceLevel = 'high' | 'medium' | 'low';

const getConfidenceLevel = (
  confidence: ExtractionConfidence,
): ExtractionConfidenceLevel => {
  if (confidence.value >= 0.85) {
    return 'high';
  }

  if (confidence.value >= 0.6) {
    return 'medium';
  }

  return 'low';
};

const confidenceColors: Record<
  ExtractionConfidenceLevel,
  ExtractionConfidenceBadgeColor
> = {
  high: 'positive',
  medium: 'warning',
  low: 'negative',
};

export const ExtractionConfidenceBadge: React.FC<{
  resource?: { extension?: fhir.Extension[] };
}> = ({ resource }) => {
  const { formatMessage } = useIntl();
  const confidence = getExtractionConfidence(resource);

  if (!confidence) {
    return null;
  }

  const confidenceLevel = getConfidenceLevel(confidence);
  const confidencePercent = formatMessage(
    messages.extractionConfidencePercent,
    {
      value: Math.round(confidence.value * 100),
    },
  );
  const tooltipParts = [
    formatMessage(messages.extractionConfidenceTooltip, {
      confidence: confidencePercent,
    }),
    confidence.rationale
      ? formatMessage(messages.extractionConfidenceRationale, {
          rationale: confidence.rationale,
        })
      : null,
    confidence.evaluatedAt
      ? formatMessage(messages.extractionConfidenceEvaluatedAt, {
          evaluatedAt: moment(confidence.evaluatedAt).format('lll'),
        })
      : null,
  ].filter((part): part is string => !!part);
  const tooltipTitle = tooltipParts.join('\n');

  return (
    <Tooltip title={tooltipTitle}>
      <Chip
        color={confidenceColors[confidenceLevel]}
        label={formatMessage(messages.extractionConfidenceLabel, {
          confidence: confidencePercent,
        })}
        aria-label={tooltipTitle}
        title={tooltipTitle}
        data-confidence-level={confidenceLevel}
      />
    </Tooltip>
  );
};
